import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
} from '@openzeppelin/ui-components';
import { cn } from '@openzeppelin/ui-utils';
import { Icon } from '@ascend/shared/components';
import { CLIENT_MARGIN_ACCOUNTS, CLIENT_AUCTIONS_BY_MARGIN_ACCOUNT } from '../data/mockAdapter';

// ─── Helpers ────────────────────────────────────────────────────────────────────

type HealthStatus = 'healthy' | 'at-risk' | 'liquidated';

function getHealthColor(hf: string): string {
  const val = parseFloat(hf);
  if (isNaN(val)) return 'text-muted-foreground';
  if (val === 0) return 'text-red-600';
  if (val < 1.5) return 'text-amber-600';
  return 'text-green-600';
}

function StatusBadge({ status }: { status: HealthStatus }) {
  const styles: Record<HealthStatus, string> = {
    healthy: 'bg-green-100 text-green-700',
    'at-risk': 'bg-amber-100 text-amber-700',
    liquidated: 'bg-red-100 text-red-700',
  };
  return (
    <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-medium', styles[status])}>
      {status}
    </span>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────────

export function MarginAccountDetailPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();

  const account = CLIENT_MARGIN_ACCOUNTS.find((a) => a.accountId === accountId) ?? null;

  if (!account) {
    return (
      <div className="space-y-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
          Back to Portfolio
        </Link>
        <div className="rounded-xl border border-dashed border-border p-12 text-center">
          <p className="text-sm text-muted-foreground">Margin account not found.</p>
        </div>
      </div>
    );
  }

  const totalOwed = account.borrowedUsd + account.interestAccruedUsd;
  const dailyAccrual = account.borrowedUsd * account.interestRatePct / 100 / 365;
  const ltv = account.collateralValueUsd > 0
    ? ((account.borrowedUsd / account.collateralValueUsd) * 100).toFixed(1)
    : '0';
  const collateralEntries = Object.entries(account.collateralAmounts);
  const hf = parseFloat(account.healthFactor);
  const isLiquidated = account.status === 'liquidated';
  const relatedAuctions = CLIENT_AUCTIONS_BY_MARGIN_ACCOUNT[account.accountId] ?? [];

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Back link + status */}
      <div className="flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
          Back to Portfolio
        </Link>
        <div className="flex items-center gap-2">
          <span
            className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700"
            title="Liquidation shortfalls covered by the Decentralized Default Fund"
          >
            Reserve covered
          </span>
          <StatusBadge status={account.status} />
        </div>
      </div>

      <div>
        <h1 className="text-xl sm:text-2xl font-bold">{account.facilityName}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Margin account {account.accountId} &middot; {account.collateralTokens.join(', ')} / {account.settlementToken}
        </p>
      </div>

      {/* Key Ratios — full width at top */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-border p-5">
          <div className="text-xs text-muted-foreground">Health Factor</div>
          <div className={cn('mt-1 text-2xl font-bold', getHealthColor(account.healthFactor))}>
            {account.healthFactor}
          </div>
          {hf > 0 && hf < 1.5 && (
            <div className="flex items-center gap-1 mt-1 text-xs text-amber-600">
              <Icon name="streamline:warning-triangle" className="h-3 w-3" />
              <span>At risk</span>
            </div>
          )}
        </div>
        <div className="rounded-xl border border-border p-5">
          <div className="text-xs text-muted-foreground">LTV</div>
          <div className="mt-1 text-2xl font-bold">{ltv}%</div>
          <div className="text-xs text-muted-foreground mt-1">Max {account.maxLtvPct}%</div>
        </div>
        <div className="rounded-xl border border-border p-5">
          <div className="text-xs text-muted-foreground">Total Owed</div>
          <div className="mt-1 text-2xl font-bold">${totalOwed.toLocaleString()}</div>
          <div className="flex items-center gap-1 mt-1 text-xs text-amber-600">
            <Icon name="streamline:graph-bar-increase" className="h-3 w-3" />
            <span>+${dailyAccrual.toFixed(2)}/day</span>
          </div>
        </div>
        <div className="rounded-xl border border-border p-5">
          <div className="text-xs text-muted-foreground">Liq. Threshold</div>
          <div className="mt-1 text-2xl font-bold">{account.liquidationThresholdPct}%</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        {/* Left column */}
        <div className="space-y-6">
          {/* Health Factor Detail */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Health Factor</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-muted-foreground">Current</span>
                <span className={cn('text-3xl font-bold', getHealthColor(account.healthFactor))}>
                  {account.healthFactor}
                </span>
              </div>
              <div className="h-3 rounded-full bg-muted overflow-hidden">
                {(() => {
                  const pct = Math.min(100, Math.max(0, (hf / 3) * 100));
                  const color = hf === 0 ? 'bg-red-500' : hf < 1.5 ? 'bg-amber-500' : 'bg-green-500';
                  return <div className={cn('h-full rounded-full', color)} style={{ width: `${pct}%` }} />;
                })()}
              </div>
              <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                <span>Liquidation</span>
                <span>Safe</span>
              </div>
              {hf > 0 && hf < 1.5 && (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <Icon name="streamline:warning-triangle" className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800">
                    Health factor is approaching the liquidation threshold. Consider depositing more collateral or repaying debt.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Collateral Breakdown */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Collateral Breakdown</CardTitle>
              <CardDescription>Tokens deposited as collateral in this margin account.</CardDescription>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Token</th>
                      <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">Amount</th>
                      <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">% of Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {collateralEntries.map(([token, amount]) => (
                      <tr key={token} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-2.5 font-medium">{token}</td>
                        <td className="px-4 py-2.5 text-right">{amount}</td>
                        <td className="px-4 py-2.5 text-right text-muted-foreground">
                          {collateralEntries.length === 1 ? '100%' : `${(100 / collateralEntries.length).toFixed(0)}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-sm text-right">
                Total Value: <span className="font-semibold">{account.collateralValue}</span>
              </div>
            </CardContent>
          </Card>

          {/* Debt Summary */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Debt Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Principal Borrowed</span>
                  <span className="font-medium">{account.borrowedValue}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Interest Accrued</span>
                  <span className="flex items-center gap-1 font-medium text-amber-600">
                    {account.interestAccrued}
                    <Icon name="streamline:graph-bar-increase" className="h-3 w-3" />
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Interest Rate</span>
                  <span className="font-medium">{account.interestRatePct}% APR</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Daily Accrual</span>
                  <span className="font-medium text-amber-600">~${dailyAccrual.toFixed(2)}/day</span>
                </div>
                <div className="flex justify-between border-t border-border pt-3">
                  <span className="font-semibold">Total Owed</span>
                  <span className="text-2xl font-bold">${totalOwed.toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Account Info */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Account Info</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Facility</span>
                <span className="font-medium">{account.facilityName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Settlement</span>
                <span className="font-medium">{account.settlementToken}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Collateral</span>
                <span className="font-medium">{account.collateralTokenText}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Interest Rate</span>
                <span className="font-medium">{account.interestRatePct}% APR</span>
              </div>
            </CardContent>
          </Card>

          {/* Total Owed */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Total Owed</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-2">
              <div className="text-3xl font-bold">${totalOwed.toLocaleString()}</div>
              <div className="flex items-center gap-1 text-sm text-amber-600">
                <Icon name="streamline:graph-bar-increase" className="h-3.5 w-3.5" />
                <span>+${dailyAccrual.toFixed(2)}/day</span>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader className="p-6">
              <CardTitle className="text-base">Actions</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-2">
              {isLiquidated ? (
                <>
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
                    <p className="text-sm text-red-800 font-medium">This account has been liquidated</p>
                    <p className="text-xs text-red-600 mt-1">No further actions are available.</p>
                  </div>
                  {relatedAuctions.map((auction) => (
                    <Button
                      key={auction.id}
                      variant="outline"
                      className="w-full"
                      onClick={() => navigate(`/liquidations/auction/${auction.id}`)}
                    >
                      <Icon name="streamline:legal-justice-hammer-hammer-work-legal-mallet-office-company-gavel-justice-judge-arbitration-court" className="mr-2 h-4 w-4" />
                      View Auction {auction.id}
                    </Button>
                  ))}
                </>
              ) : (
                <>
                  <Button className="w-full" onClick={() => navigate(`/deposit?facilityId=${account.facilityId}`)}>
                    <Icon name="streamline:interface-arrows-button-to-bottom-arrow-down-line-to-bottom" className="mr-2 h-4 w-4" />
                    Deposit Collateral
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => navigate(`/withdraw?accountId=${account.accountId}`)}>
                    <Icon name="streamline:interface-arrows-button-to-top-arrow-up-line-to-top" className="mr-2 h-4 w-4" />
                    Withdraw Collateral
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => navigate(`/borrow?accountId=${account.accountId}`)}>
                    <Icon name="streamline:bank" className="mr-2 h-4 w-4" />
                    Borrow
                  </Button>
                  <Button variant="outline" className="w-full" onClick={() => navigate(`/repay?accountId=${account.accountId}`)}>
                    <Icon name="streamline:interface-arrows-synchronize-arrows-loading-load-sync-synchronize-arrow-reload" className="mr-2 h-4 w-4" />
                    Repay
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
